import { useRef, useState } from 'react';
import type { ComponentPropsWithRef, KeyboardEvent, ReactNode } from 'react';

import { cn } from '../../../lib/cn';
import { ChoiceField } from './ChoiceField';
import { useFieldIds } from './useFieldIds';

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps extends Omit<ComponentPropsWithRef<'div'>, 'children' | 'onChange'> {
  /** Shared `name` for every radio — this is what makes the options one group natively. */
  name: string;
  options: RadioOption[];
  /** Controlled value. Leave it undefined to let the group own the selection. */
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  label: ReactNode;
  error?: string | null;
  success?: string | null;
  hint?: string | null;
  required?: boolean;
  disabled?: boolean;
  /** Defaults to `vertical`. */
  orientation?: 'vertical' | 'horizontal';
  id?: string;
}

/**
 * A group of real `<input type="radio">`s behind a `<fieldset>`/`<legend>`.
 *
 * The mark is a square drawn entirely from tokens — copper fill, graphite edge —
 * rather than the platform's round dot, because the Constructivist language is
 * built on square corners.
 *
 * The keyboard contract is split deliberately, and that split is the point of
 * this comment: arrow-key movement AND the roving tabindex (only the checked
 * radio is tabbable; the first one is when none is checked) come from the native
 * radio group sharing one `name`, so nothing here reimplements them — a
 * reimplementation would only diverge from the platform. Home and End are the
 * two keys native radios do NOT handle, so they are added here, and they
 * `preventDefault()` because Home/End otherwise scroll the document.
 */
export function RadioGroup({
  name,
  options,
  value,
  onValueChange,
  defaultValue,
  label,
  error,
  success,
  hint,
  required = false,
  disabled = false,
  orientation = 'vertical',
  id,
  className,
  ...rest
}: RadioGroupProps) {
  const ids = useFieldIds(id, { error, success, hint });
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? '');
  const selected = value === undefined ? uncontrolled : value;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  function select(next: string) {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Home' && event.key !== 'End') return;

    const enabled = options.filter((option) => !option.disabled);
    if (enabled.length === 0) return;

    const target = event.key === 'Home' ? enabled[0] : enabled[enabled.length - 1];
    const input = inputRefs.current[options.indexOf(target)];
    if (!input) return;

    event.preventDefault();
    input.focus();
    select(target.value);
  }

  return (
    <ChoiceField
      {...rest}
      id={ids.id}
      label={label}
      required={required}
      disabled={disabled}
      error={error}
      success={success}
      hint={hint}
      group
      role="radiogroup"
      aria-invalid={ids.invalid || undefined}
      aria-required={required || undefined}
      aria-orientation={orientation}
      aria-describedby={ids.describedBy}
      className={className}
    >
      <div
        onKeyDown={handleKeyDown}
        className={cn(
          'flex min-w-0',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap items-start gap-x-6',
        )}
      >
        {options.map((option, index) => {
          const optionId = `${ids.id}-${index}`;
          const optionDisabled = disabled || option.disabled === true;

          return (
            <div key={option.value} className="flex min-w-0 items-start gap-3">
              {/* The hit-target overlay lives on this wrapper, not on the input: `::after`
                  is not generated for a replaced element, so an overlay class on the
                  `<input>` would render nothing. The 44px floor is doubled by `min-h-11`
                  on the label, which is the real click surface for the whole row. */}
              <span className="dhd-target relative mt-2.5 flex shrink-0">
                <input
                  ref={(node) => {
                    inputRefs.current[index] = node;
                  }}
                  id={optionId}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={selected === option.value}
                  disabled={optionDisabled}
                  onChange={() => select(option.value)}
                  className={cn(
                    'peer relative size-5 shrink-0 appearance-none rounded-none border-2 bg-surface',
                    'transition-[background-color,border-color,box-shadow] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                    'disabled:cursor-not-allowed disabled:opacity-45',
                    ids.invalid
                      ? 'border-danger checked:border-danger'
                      : 'border-line-strong checked:border-primary',
                    !optionDisabled && (ids.invalid ? 'hover:border-danger' : 'hover:border-primary'),
                  )}
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 scale-0 rounded-none',
                    'transition-[transform,scale,background-color] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                    'peer-checked:scale-100 peer-disabled:opacity-45',
                    ids.invalid ? 'bg-danger' : 'bg-primary',
                  )}
                />
              </span>

              <label
                htmlFor={optionId}
                className={cn(
                  'flex min-h-11 min-w-0 flex-1 select-none flex-col gap-0.5 py-3',
                  optionDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                <span className={cn('text-sm leading-snug', optionDisabled ? 'text-muted' : 'text-body')}>
                  {option.label}
                </span>
                {option.description ? (
                  <span className="text-xs leading-snug text-muted">{option.description}</span>
                ) : null}
              </label>
            </div>
          );
        })}
      </div>
    </ChoiceField>
  );
}
